/* WHAT IS IN THIS PHOTOGRAPH BESIDES THE PHOTOGRAPH.
 *
 * /remove-exif-data takes the metadata off, and that is the right default --
 * step two of that page says so: "there is no box to remember to tick". What
 * it cannot do is answer the question people actually ask first, which is
 * whether there was anything to worry about. Measured on a real phone
 * photograph: a OnePlus 13 writes the make, the model, the second it was
 * taken and the GPS position to within a few metres, and nothing on the
 * device shows you any of it.
 *
 * NOTHING IS UPLOADED. The bytes are read here, on the device, the way
 * lib/svgRaster.js and lib/tileSplit.js work -- which matters more here than
 * anywhere else on the site: a page that exists to tell you your photograph
 * is carrying your home address must not be the thing that sends it
 * somewhere. There is no server half to this tool and there must never be.
 *
 * ONE TIFF PARSER, FOUR CONTAINERS. EXIF is a TIFF structure that different
 * formats carry in different wrappers -- a JPEG APP1 segment, a PNG `eXIf`
 * chunk, a WebP `EXIF` chunk, an ISO-BMFF item for HEIC. So locating the
 * block is per-container and reading it is shared; a second parser would be a
 * second set of endianness bugs.
 */

/* Tags worth naming. Deliberately a SHORT list: this is not a metadata
 * editor, it is an answer to "is anything here that I would not want to
 * share". Everything else is counted, not listed. */
const IFD0 = {
    0x010f: "make", 0x0110: "model", 0x0131: "software",
    0x0132: "dateTime", 0x013b: "artist", 0x8298: "copyright",
    0x010e: "description",
}
const EXIF_IFD = {
    0x9003: "dateTaken", 0x9004: "dateDigitised", 0xa434: "lens",
    0x8827: "iso", 0x829a: "exposureTime", 0x829d: "fNumber",
    0xa420: "imageUniqueId", 0x9291: "subSecond",
}
const GPS_IFD = {
    1: "latRef", 2: "lat", 3: "lonRef", 4: "lon", 5: "altRef", 6: "alt",
    7: "gpsTime", 17: "direction", 29: "gpsDate",
}
const EXIF_POINTER = 0x8769
const GPS_POINTER = 0x8825

// bytes per component, indexed by TIFF type
const TYPE_SIZE = {1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8, 11: 4, 12: 8}

class Reader {
    constructor(bytes, base, little) {
        this.b = bytes
        this.base = base
        this.le = little
    }
    u16(at) {
        const b = this.b, i = this.base + at
        return this.le ? b[i] | (b[i + 1] << 8) : (b[i] << 8) | b[i + 1]
    }
    u32(at) {
        const b = this.b, i = this.base + at
        return (this.le
            ? b[i] | (b[i + 1] << 8) | (b[i + 2] << 16) | (b[i + 3] << 24)
            : (b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0
    }
    /* FLOAT and DOUBLE are not what the EXIF spec asks for -- ExposureTime
     * and FNumber are RATIONAL, and every real camera writes them that way
     * (the OnePlus fixture this was developed against does). But some
     * software writes IEEE floats instead, Pillow among them, and a reader
     * that cannot cope prints "f/null" at somebody. Read through a DataView
     * because there is no sane way to assemble an IEEE754 double by hand. */
    f(at, bytes) {
        const b = this.b, i = this.base + at
        const view = new DataView(b.buffer, b.byteOffset + i, bytes)
        return bytes === 8 ? view.getFloat64(0, this.le) : view.getFloat32(0, this.le)
    }
    ascii(at, len) {
        let s = ""
        for (let i = 0; i < len; i++) {
            const c = this.b[this.base + at + i]
            if (c === 0) break
            s += String.fromCharCode(c)
        }
        return s.trim()
    }
}

/* One IFD entry's value, for the few types that actually turn up. A rational
 * is returned as a NUMBER, except for GPS coordinates where the three parts
 * are what the caller needs -- hence `raw`. */
function valueOf(r, entryAt, type, count) {
    const size = TYPE_SIZE[type] || 0
    const bytes = size * count
    // values of four bytes or fewer sit in the offset field itself
    const at = bytes > 4 ? r.u32(entryAt + 8) : entryAt + 8
    if (at < 0 || at + bytes > r.b.length) return null

    if (type === 2) return r.ascii(at, count)
    if (type === 3) return count === 1 ? r.u16(at) : null
    if (type === 4) return count === 1 ? r.u32(at) : null
    if ((type === 11 || type === 12) && count === 1) {
        const v = r.f(at, type === 12 ? 8 : 4)
        return isFinite(v) ? v : null
    }
    if (type === 5 || type === 10) {
        const parts = []
        for (let i = 0; i < count; i++) {
            const n = r.u32(at + i * 8), d = r.u32(at + i * 8 + 4)
            parts.push(d ? n / d : 0)
        }
        return count === 1 ? parts[0] : {raw: parts}
    }
    return null
}

function readIfd(r, at, names, out, pointers) {
    if (at <= 0 || at + 2 > r.b.length) return
    const n = r.u16(at)
    // A corrupt or misread offset can claim tens of thousands of entries;
    // 512 is far above anything real and keeps a bad file from spinning.
    if (n > 512) return
    for (let i = 0; i < n; i++) {
        const e = at + 2 + i * 12
        if (e + 12 > r.b.length) return
        const tag = r.u16(e), type = r.u16(e + 2), count = r.u32(e + 4)
        if (pointers && (tag === EXIF_POINTER || tag === GPS_POINTER)) {
            pointers[tag] = r.u32(e + 8)
            continue
        }
        const name = names[tag]
        if (!name) { out.__other = (out.__other || 0) + 1; continue }
        const v = valueOf(r, e, type, count)
        if (v !== null && v !== "") out[name] = v
    }
}

/* Degrees-minutes-seconds to a decimal degree, signed by the hemisphere. */
export function decimalDegrees(parts, ref) {
    if (!parts || !parts.raw || parts.raw.length < 3) return null
    const [d, m, s] = parts.raw
    const sign = (ref === "S" || ref === "W") ? -1 : 1
    return sign * (d + m / 60 + s / 3600)
}

/* WHERE THE TIFF BLOCK LIVES, per container.
 *
 * Returns the offset of the TIFF header (II*\0 or MM\0*), or -1. Kept
 * separate from the parsing so a new container is a new locator and nothing
 * else. */
export function findTiff(b) {
    if (!b || b.length < 12) return -1

    // JPEG: walk the segment chain looking for APP1 "Exif\0\0"
    if (b[0] === 0xff && b[1] === 0xd8) {
        let i = 2
        while (i + 4 <= b.length) {
            if (b[i] !== 0xff) break
            const marker = b[i + 1]
            if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) { i += 2; continue }
            if (marker === 0xda || marker === 0xd9) break      // image data starts; no EXIF beyond
            const len = (b[i + 2] << 8) | b[i + 3]
            if (len < 2) break
            if (marker === 0xe1 && i + 10 < b.length
                && b[i + 4] === 0x45 && b[i + 5] === 0x78 && b[i + 6] === 0x69 && b[i + 7] === 0x66
                && b[i + 8] === 0x00) {
                return i + 10
            }
            i += 2 + len
        }
        return -1
    }

    // PNG: an `eXIf` chunk holds the TIFF block directly
    if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) {
        let i = 8
        while (i + 8 <= b.length) {
            const len = ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0
            const type = String.fromCharCode(b[i + 4], b[i + 5], b[i + 6], b[i + 7])
            if (type === "eXIf") return i + 8
            if (type === "IEND" || len > b.length) break
            i += 12 + len
        }
        return -1
    }

    // WebP (RIFF): an `EXIF` chunk, likewise
    if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46
        && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) {
        let i = 12
        while (i + 8 <= b.length) {
            const type = String.fromCharCode(b[i], b[i + 1], b[i + 2], b[i + 3])
            const len = (b[i + 4] | (b[i + 5] << 8) | (b[i + 6] << 16) | (b[i + 7] << 24)) >>> 0
            if (type === "EXIF") return i + 8
            if (len > b.length) break
            i += 8 + len + (len & 1)          // chunks are padded to even length
        }
        return -1
    }

    /* HEIC and friends. Properly this means walking meta/iloc to find the
     * Exif item's extent, which is a lot of box code for a format whose EXIF
     * payload is reliably prefixed the same way. Scanning for the "Exif\0\0"
     * prefix inside the first part of the file finds it without the walk, and
     * a false positive costs a failed parse rather than a wrong answer --
     * readExif() returns nothing if the TIFF header is not there. */
    if (b.length > 12 && b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
        const limit = Math.min(b.length - 10, 4 * 1024 * 1024)
        for (let i = 0; i < limit; i++) {
            if (b[i] === 0x45 && b[i + 1] === 0x78 && b[i + 2] === 0x69 && b[i + 3] === 0x66
                && b[i + 4] === 0x00 && b[i + 5] === 0x00) {
                const t = i + 6
                if ((b[t] === 0x49 && b[t + 1] === 0x49) || (b[t] === 0x4d && b[t + 1] === 0x4d)) return t
            }
        }
        return -1
    }

    return -1
}

/* Read what is there. Never throws: a malformed file is "nothing found",
 * because the alternative is a page about privacy showing a stack trace. */
export function readExif(bytes) {
    const empty = {found: false, device: {}, taken: {}, location: null, otherTags: 0}
    try {
        const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
        const tiff = findTiff(b)
        if (tiff < 0) return empty

        const le = b[tiff] === 0x49 && b[tiff + 1] === 0x49
        const be = b[tiff] === 0x4d && b[tiff + 1] === 0x4d
        if (!le && !be) return empty
        const r = new Reader(b, tiff, le)
        if (r.u16(2) !== 42) return empty

        const ifd0 = {}, exif = {}, gps = {}, pointers = {}
        readIfd(r, r.u32(4), IFD0, ifd0, pointers)
        if (pointers[EXIF_POINTER]) readIfd(r, pointers[EXIF_POINTER], EXIF_IFD, exif, null)
        if (pointers[GPS_POINTER]) readIfd(r, pointers[GPS_POINTER], GPS_IFD, gps, null)

        const lat = decimalDegrees(gps.lat, gps.latRef)
        const lon = decimalDegrees(gps.lon, gps.lonRef)
        const located = lat !== null && lon !== null && !(lat === 0 && lon === 0)

        /* FOUND MEANS "THERE IS SOMETHING TO TELL YOU", not "a TIFF header
         * parsed". Reported from a real iPhone: the page said "This photo is
         * carrying some information about you" and listed nothing underneath.
         *
         * Reproduced exactly -- a JPEG whose EXIF block holds ONLY
         * Orientation, which is what Safari's transcode writes when it keeps
         * the picture upright and drops the rest. A TIFF header parses, every
         * field the page names is empty, and the verdict asserts a finding it
         * cannot show. "Nothing found in this one" is both true and the
         * reassuring answer, which is what makes getting it wrong worse than
         * saying nothing.
         *
         * Orientation is deliberately NOT counted. It says which way up the
         * picture is and nothing about the person who took it, so a file
         * carrying only that is a file carrying nothing worth reporting. */
        const anything = !!(located || ifd0.make || ifd0.model || ifd0.software
            || ifd0.artist || ifd0.copyright || ifd0.description
            || exif.dateTaken || ifd0.dateTime || exif.lens
            || exif.iso || exif.exposureTime || exif.fNumber)
        if (!anything) return empty

        return {
            found: true,
            device: {
                make: ifd0.make || null,
                model: ifd0.model || null,
                software: ifd0.software || null,
                lens: exif.lens || null,
            },
            taken: {
                when: exif.dateTaken || ifd0.dateTime || null,
                iso: exif.iso || null,
                exposureTime: exif.exposureTime || null,
                fNumber: exif.fNumber || null,
            },
            location: located ? {
                lat, lon,
                altitude: typeof gps.alt === "number" ? gps.alt : null,
                direction: typeof gps.direction === "number" ? gps.direction : null,
            } : null,
            author: ifd0.artist || ifd0.copyright || null,
            otherTags: (ifd0.__other || 0) + (exif.__other || 0) + (gps.__other || 0),
        }
    } catch (err) {
        return empty
    }
}

/* NOTHING IN EXIF IS STORED THE WAY A PERSON READS IT, and every one of
 * these is a small translation nobody should have to do in their head:
 *
 *   "2026:09:22 14:43:45"   is a date with colons where the dots go
 *   0.006993006993006993    is a shutter speed, and means 1/143
 *   1.6                     is an aperture, and means f/1.6
 *   50.94414722222222       is a latitude with eleven digits nobody needs
 *
 * The raw values stay on the object for anything that wants them; these are
 * what the page prints.
 */

/* EXIF writes the date with colons in the date part -- "2026:09:22 14:43:45"
 * -- which is not a format any Date parser accepts and not one anybody reads
 * comfortably either. Returned as its parts so the page can lay them out;
 * null when the string is not the shape EXIF promises, because guessing at a
 * malformed date is how you print a confident wrong answer. */
export function readableWhen(raw) {
    const m = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(String(raw || ""))
    if (!m) return null
    const [, y, mo, d, h, mi, sec] = m
    const months = ["January", "February", "March", "April", "May", "June",
                    "July", "August", "September", "October", "November", "December"]
    const name = months[Number(mo) - 1]
    if (!name) return null
    return {
        date: `${Number(d)} ${name} ${y}`,
        time: `${h}:${mi}:${sec}`,
        iso: `${y}-${mo}-${d}T${h}:${mi}:${sec}`,
    }
}

/* A shutter speed is stored as a fraction of a second and read as one: 1/143
 * rather than 0.006993006993006993. Above a second it is said plainly, since
 * "1/0.5" is nonsense. */
export function readableExposure(seconds) {
    if (typeof seconds !== "number" || !(seconds > 0)) return null
    if (seconds >= 1) return `${Math.round(seconds * 10) / 10} s`
    return `1/${Math.round(1 / seconds)} s`
}

/* Degrees, minutes and seconds -- which is how the camera wrote it and how a
 * map says it back to you. The decimal form is what Google Maps takes, so the
 * page prints both and links with the decimal. */
export function readableCoord(deg, isLat) {
    if (typeof deg !== "number" || !isFinite(deg)) return null
    const hemi = isLat ? (deg < 0 ? "S" : "N") : (deg < 0 ? "W" : "E")
    const a = Math.abs(deg)
    const d = Math.floor(a)
    const mFloat = (a - d) * 60
    const m = Math.floor(mFloat)
    const sec = (mFloat - m) * 60
    return {
        dms: `${d}° ${m}' ${sec.toFixed(2)}" ${hemi}`,
        // six places is about 10 cm -- further is noise, and fewer loses the
        // precision that makes the point
        decimal: (Math.round(deg * 1e6) / 1e6).toFixed(6),
    }
}

/* A bearing in degrees, said as a compass point as well. */
export function readableDirection(deg) {
    if (typeof deg !== "number" || !isFinite(deg)) return null
    const points = ["north", "north-east", "east", "south-east",
                    "south", "south-west", "west", "north-west"]
    const i = Math.round(((deg % 360) + 360) % 360 / 45) % 8
    return {degrees: Math.round(deg), point: points[i]}
}

/* WHERE THE LINK GOES, and it is a LINK rather than a lookup.
 *
 * Nothing is sent anywhere until somebody clicks it -- which is the whole
 * difference between this and reverse-geocoding the position on load, and is
 * why it is defensible on a page about not leaking your location. Recorded as
 * an instructed decision: it was raised that any map provider learns the
 * coordinates, and the owner chose the link knowingly, on the grounds that it
 * is the visitor's own photograph and their own choice to follow it.
 *
 * The caller renders it with rel="noopener noreferrer", so following it does
 * not also tell Google which page it was followed from.
 */
export function mapsUrl(lat, lon) {
    if (typeof lat !== "number" || typeof lon !== "number") return null
    return `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lon.toFixed(6)}`
}

/* The sentence the page leads with, as a list of CATEGORY KEYS rather than
 * prose -- the words live in the catalogue like every other string here. */
export function findingKeys(info) {
    if (!info || !info.found) return []
    const out = []
    if (info.location) out.push("location")
    if (info.taken && info.taken.when) out.push("when")
    if (info.device && (info.device.make || info.device.model)) out.push("device")
    if (info.device && info.device.software) out.push("software")
    if (info.author) out.push("author")
    return out
}
